import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { loginFormSchema, type LoginFormData } from '@/lib/schema/authSchema';
import { loginRequest } from '@/services/auth/authService';
import { useAuthActions } from '@/store/authStore';
import { useTranslation } from '@/store/languageStore';
import AuthenticationCode from '@/components/auth/AuthenticationCode';
import LoginLayout from '@/components/auth/LoginLayout';
import api from '@/services/api/client';
import { useSsoDomainLogin } from '@/hooks/useSsoDomainLogin';
import { buildSsoInitiateUrl, type SsoDiscoverInfo } from '@/utils/ssoRedirect';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setLoginDetails } = useAuthActions();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [requiresEmailVerification, setRequiresEmailVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendingVerification, setResendingVerification] = useState(false);
  const [signupOrgLinked, setSignupOrgLinked] = useState(false);
  const [preferPassword, setPreferPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
    setValue,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const emailValue = watch('email') || '';
  const redirectTo = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || '/';
  const { sso, checking, cameBack, startSso } = useSsoDomainLogin(emailValue, {
    enabled: !requiresEmailVerification,
    preferPassword,
    redirectPath: redirectTo,
  });

  useEffect(() => {
    setPreferPassword(false);
  }, [emailValue]);

  useEffect(() => {
    const state = location.state as {
      email?: string;
      requiresVerification?: boolean;
      message?: string;
      organizationLinkedByDomain?: boolean;
    } | null;
    if (state?.requiresVerification) {
      setRequiresEmailVerification(true);
      setVerificationEmail(state.email || '');
      setSignupOrgLinked(state.organizationLinkedByDomain === true);
      if (state.email) {
        setValue('email', state.email);
      }
    }
  }, [location.state, setValue]);

  const goToSso = (info: SsoDiscoverInfo, email: string) => {
    startSso(info, email);
  };

  const onSubmit = async (form: LoginFormData) => {
    const trimmed = form.email.trim();

    if (!preferPassword) {
      if (sso) {
        goToSso(sso, trimmed);
        return;
      }
      setIsLoading(true);
      try {
        const res = await api.get('/api/auth/federation/discover', { params: { email: trimmed } });
        const data = res.data?.data;
        const method = data?.authMethod;
        if (data?.ssoRequired && data?.organizationId && (method === 'OIDC' || method === 'SAML2')) {
          goToSso(
            {
              organizationId: String(data.organizationId),
              organizationName: data.organizationName,
              authMethod: method,
            },
            trimmed
          );
          return;
        }
      } catch {
        // Fail-open mot lösenordsinloggning
      } finally {
        setIsLoading(false);
      }
    }

    if (!form.password || form.password.length < 6) {
      setError('password', { type: 'manual', message: t('login.passwordRequired') });
      return;
    }

    setIsLoading(true);

    try {
      const response = await loginRequest({
        email: form.email,
        password: form.password,
        preferPassword: preferPassword || undefined,
      });

      if (response.status === 200 && response.data) {
        const responseData = response.data;

        if (responseData.success) {
          const data = responseData.data as any;

          if (data?.accessToken) {
            setLoginDetails({
              accessToken: data.accessToken,
              refreshToken: data.refreshToken,
              user: data.user,
            });

            if (data?.authMethodChanged) {
              toast.success(responseData.message || t('auth.authMethodChanged') || 'Autentiseringsmetod har ändrats');
            } else {
              toast.success(t('login.welcomeMessage'));
            }
            navigate('/');
          } else if (data?.requiresEmailVerification) {
            setRequiresEmailVerification(true);
            setVerificationEmail(data.email || '');
            toast.error(responseData.error || 'Du måste verifiera din e-postadress innan du kan logga in.');
          } else if (data?.email) {
            setUserEmail(data.email);
            setIsVerified(true);
            if (data?.authMethodChanged) {
              toast.success(responseData.message || t('auth.authMethodChanged') || 'Autentiseringsmetod har ändrats');
            } else {
              toast.success(responseData.message || t('auth.otpSent') || 'Verifieringskod skickad');
            }
          } else if (data?.requiresSaml || data?.requiresOidc) {
            const orgId = data?.organizationId;
            if (orgId) {
              window.location.replace(
                buildSsoInitiateUrl({
                  organizationId: String(orgId),
                  authMethod: data.requiresSaml ? 'SAML2' : 'OIDC',
                  email: trimmed,
                  redirect: redirectTo,
                })
              );
            } else {
              toast.error(t('auth.samlRequired') || 'SSO krävs för denna organisation men organisation kunde inte bestämmas.');
            }
          }
        }
      }
    } catch (error: any) {
      if (error.response?.data?.requiresEmailVerification) {
        setRequiresEmailVerification(true);
        setVerificationEmail(error.response?.data?.email || form.email);
        toast.error(error.response?.data?.error || 'Du måste verifiera din e-postadress innan du kan logga in.');
      } else {
        const errorMessage =
          error.response?.data?.error || error.response?.data?.message || t('login.loginError');

        toast.error(errorMessage);

        setError('password', {
          type: 'manual',
          message: errorMessage,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!verificationEmail) return;

    try {
      setResendingVerification(true);
      await api.post('/api/auth/resend-verification-email', { email: verificationEmail });
      toast.success(t('login.resendVerificationSuccess'));
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('login.resendVerificationError'));
    } finally {
      setResendingVerification(false);
    }
  };

  if (isVerified) {
    return (
      <LoginLayout>
        <AuthenticationCode
          setIsVerified={setIsVerified}
          email={userEmail}
          type="LOGIN"
        />
      </LoginLayout>
    );
  }

  const fieldClass = (hasError: boolean) =>
    `w-full h-14 rounded-[10px] border-[1.5px] bg-white px-3.5 text-[17px] text-[#10242A] placeholder:text-[#6B7F84] transition focus:border-[#1FB8A8] focus:outline-none focus:shadow-[0_0_0_4px_rgba(31,184,168,0.18)] disabled:opacity-60 ${
      hasError ? 'border-[#C8473A]' : 'border-[#D6DFE1]'
    }`;

  const showPasswordField = preferPassword || (!sso && !checking);
  const orgLabel = sso?.organizationName || t('auth.ssoLogin');
  const ssoBusy = Boolean(sso && !preferPassword && !cameBack);

  return (
    <LoginLayout>
      <h2 className="mb-1.5 text-[36px] font-extrabold tracking-[-0.02em] text-[#10242A]">{t('login.title')}</h2>
      <p className="mb-8 text-[18px] text-[#6B7F84]">{t('login.subtitle')}</p>

      {requiresEmailVerification && (
        <div className="mt-6 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <h3 className="text-sm font-medium text-yellow-800">{t('login.verifyEmailTitle')}</h3>
          <div className="mt-2 space-y-2 text-sm text-yellow-700">
            <p>
              {verificationEmail
                ? t('login.verifyEmailBodyWithEmail').replace('{email}', verificationEmail)
                : t('login.verifyEmailBody')}
            </p>
            <p>{signupOrgLinked ? t('login.verifyOrgLinkedNote') : t('login.verifyManualOrgNote')}</p>
            {verificationEmail && (
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendingVerification}
                className="text-sm font-medium text-[#0f8f84] underline disabled:opacity-50"
              >
                {resendingVerification ? t('login.resendingVerification') : t('login.resendVerification')}
              </button>
            )}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-[18px]">
          <label htmlFor="email" className="mb-2 block text-[16px] font-semibold text-[#10242A]">
            {t('login.emailLabel')}
          </label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            {...register('email')}
            className={fieldClass(Boolean(errors.email))}
            placeholder={t('login.emailPlaceholder')}
            disabled={isLoading || ssoBusy}
          />
          {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>}
          {ssoBusy && <p className="mt-2 text-sm text-[#0F8F82]">{t('auth.ssoRedirect')}</p>}
          {sso && cameBack && !preferPassword && (
            <p className="mt-2 text-sm text-[#6B7F84]">{t('auth.ssoContinue').replace('{org}', orgLabel)}</p>
          )}
        </div>

        {showPasswordField && (
          <div className="mb-[18px]">
            <label htmlFor="password" className="mb-2 block text-[16px] font-semibold text-[#10242A]">
              {t('login.passwordLabel')}
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                {...register('password')}
                className={`${fieldClass(Boolean(errors.password))} pr-[52px]`}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-1.5 top-1/2 h-10 -translate-y-1/2 rounded-md bg-transparent px-2.5 text-[15px] font-semibold text-[#6B7F84] hover:bg-[#F6F8F8] hover:text-[#10242A]"
                aria-pressed={showPassword}
                aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                disabled={isLoading}
              >
                {showPassword ? t('login.hidePassword') : t('login.showPassword')}
              </button>
            </div>
            {errors.password && <p className="mt-1 text-sm text-[#C8473A]">{errors.password.message}</p>}
          </div>
        )}

        {showPasswordField && (
          <div className="mb-[26px] mt-1 flex justify-end text-[16px]">
            <Link to="/reset-password" className="font-semibold text-[#0F8F82] hover:underline">
              {t('login.forgotPassword')}
            </Link>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading || checking || ssoBusy}
          className="h-14 w-full rounded-[10px] bg-[linear-gradient(180deg,#22C4B3_0%,#1FB8A8_60%,#0F8F82_100%)] text-[18px] font-bold text-white shadow-[0_1px_2px_rgba(8,29,35,0.2),inset_0_1px_0_rgba(255,255,255,0.25)] transition hover:brightness-105 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50"
        >
          {sso && !preferPassword
            ? t('auth.useSsoInstead')
            : isLoading
              ? t('login.loggingIn')
              : t('login.loginButton')}
        </button>

        {sso && !preferPassword && (
          <button
            type="button"
            onClick={() => setPreferPassword(true)}
            className="mt-3 w-full text-sm text-[#6B7F84] hover:text-[#10242A]"
          >
            {t('auth.loginWithPassword')}
          </button>
        )}
      </form>

      <p className="mt-7 text-center text-[16px] text-[#6B7F84]">
        {t('login.noAccount')}{' '}
        <Link to="/signup" className="font-semibold text-[#0F8F82] hover:underline">
          {t('login.createAccount')}
        </Link>
      </p>
    </LoginLayout>
  );
};

export default Login;
